import React, { useState, useEffect } from 'react';
import { securityAPI, clientsAPI } from '../../services/api';
import './MtaRegister.css';

const ACTION_TYPES = ['All', 'Add', 'Remove', 'Temp Cover', 'Reinstatement', 'Change of Vehicle'];

interface MtaRow {
  id: number;
  dateTime: string;
  client: string;
  registration: string;
  actionType: string;
  submittedBy: string;
  emailSent: boolean;
  confirmationStatus: 'Sent' | 'Failed' | 'Unknown';
  notes: string;
}

interface ClientOption {
  id: number;
  companyName: string;
}

const isoDate = (d: Date) => d.toISOString().slice(0, 10);

const defaultTo   = () => isoDate(new Date());
const defaultFrom = () => {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return isoDate(d);
};

const MtaRegister: React.FC = () => {
  const [clients, setClients]         = useState<ClientOption[]>([]);
  const [clientId, setClientId]       = useState('');
  const [from, setFrom]               = useState(defaultFrom());
  const [to, setTo]                   = useState(defaultTo());
  const [actionType, setActionType]   = useState('All');

  const [rows, setRows]               = useState<MtaRow[]>([]);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState<string | null>(null);
  const [searched, setSearched]       = useState(false);

  // Load clients for filter dropdown
  useEffect(() => {
    clientsAPI.getAll()
      .then(res => setClients(res.data ?? []))
      .catch(() => {});
  }, []);

  const search = async () => {
    setLoading(true);
    setError(null);
    setSearched(true);
    try {
      const res = await securityAPI.getMtaRegister({
        clientId: clientId || undefined,
        from,
        to,
        actionType: actionType === 'All' ? undefined : actionType,
      });
      setRows(res.data?.entries ?? []);
    } catch {
      setError('Failed to load MTA register. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const downloadCsv = () => {
    const headers = [
      'Date/Time', 'Client', 'Registration', 'Action Type',
      'Submitted By', 'Email Sent', 'Confirmation Status', 'Notes',
    ];
    const csvRows = [
      headers.join(','),
      ...rows.map(r => [
        `"${new Date(r.dateTime).toLocaleString('en-GB')}"`,
        `"${r.client}"`,
        r.registration,
        `"${r.actionType}"`,
        `"${r.submittedBy}"`,
        r.emailSent ? 'Yes' : 'No',
        r.confirmationStatus,
        `"${r.notes ?? ''}"`,
      ].join(',')),
    ];
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `mta-register-${from}-to-${to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const statusIcon = (status: MtaRow['confirmationStatus'], emailSent: boolean) => {
    if (emailSent && status === 'Sent') {
      return <span className="mta-status mta-status--ok" title="Email sent">&#10003;</span>;
    }
    return <span className="mta-status mta-status--warn" title={status}>&#9888;</span>;
  };

  return (
    <div className="mta">
      <div className="mta-card">
        <h3 className="mta-heading">MTA Register — Insurer Notification Log</h3>
        <p className="mta-description">
          Mid-term adjustment register showing all policy changes notified to insurers.
        </p>

        {/* Filters */}
        <div className="mta-filters">
          <div className="mta-filter-group">
            <label className="mta-label" htmlFor="mta-client">Client</label>
            <select
              id="mta-client"
              className="mta-select"
              value={clientId}
              onChange={e => setClientId(e.target.value)}
            >
              <option value="">All Clients</option>
              {clients.map(c => (
                <option key={c.id} value={String(c.id)}>{c.companyName}</option>
              ))}
            </select>
          </div>

          <div className="mta-filter-group">
            <label className="mta-label" htmlFor="mta-from">From</label>
            <input
              id="mta-from"
              type="date"
              className="mta-date-input"
              value={from}
              onChange={e => setFrom(e.target.value)}
            />
          </div>

          <div className="mta-filter-group">
            <label className="mta-label" htmlFor="mta-to">To</label>
            <input
              id="mta-to"
              type="date"
              className="mta-date-input"
              value={to}
              onChange={e => setTo(e.target.value)}
            />
          </div>

          <div className="mta-filter-group">
            <label className="mta-label" htmlFor="mta-action">Action Type</label>
            <select
              id="mta-action"
              className="mta-select"
              value={actionType}
              onChange={e => setActionType(e.target.value)}
            >
              {ACTION_TYPES.map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>

          <button
            className="mta-search-btn"
            onClick={search}
            disabled={loading}
          >
            {loading ? 'Loading…' : 'Search'}
          </button>
        </div>

        {error && <p className="mta-error">{error}</p>}

        {!loading && searched && !error && (
          <>
            <div className="mta-table-header">
              <span className="mta-result-count">
                {rows.length} {rows.length === 1 ? 'record' : 'records'} found
              </span>
              {rows.length > 0 && (
                <button className="mta-csv-btn" onClick={downloadCsv}>
                  Export CSV
                </button>
              )}
            </div>

            {rows.length === 0 ? (
              <p className="mta-empty">No records match the selected filters.</p>
            ) : (
              <div className="mta-table-wrap">
                <table className="mta-table">
                  <thead>
                    <tr>
                      <th>Date / Time</th>
                      <th>Client</th>
                      <th>Registration</th>
                      <th>Action Type</th>
                      <th>Submitted By</th>
                      <th>Notified</th>
                      <th>Confirmation</th>
                      <th>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(r => (
                      <tr key={r.id}>
                        <td className="mta-nowrap">
                          {new Date(r.dateTime).toLocaleString('en-GB', {
                            day: '2-digit', month: 'short', year: 'numeric',
                            hour: '2-digit', minute: '2-digit',
                          })}
                        </td>
                        <td>{r.client}</td>
                        <td className="mta-reg">{r.registration}</td>
                        <td>
                          <span className={`mta-action-badge mta-action-badge--${r.actionType.toLowerCase().replace(/\s+/g, '-')}`}>
                            {r.actionType}
                          </span>
                        </td>
                        <td>{r.submittedBy}</td>
                        <td className="mta-center">
                          {r.emailSent
                            ? <span className="mta-status mta-status--ok" title="Email sent">&#10003;</span>
                            : <span className="mta-status mta-status--warn" title="Not sent">&#9888;</span>
                          }
                        </td>
                        <td className="mta-center">
                          {statusIcon(r.confirmationStatus, r.emailSent)}
                          <span className="mta-status-text">{r.confirmationStatus}</span>
                        </td>
                        <td className="mta-notes">{r.notes || <em className="mta-none">—</em>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {!searched && !loading && (
          <p className="mta-prompt">Use the filters above and click Search to load the register.</p>
        )}
      </div>
    </div>
  );
};

export default MtaRegister;
